import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import EmptyState from '../../components/ui/EmptyState';
import { ListSkeleton } from '../../components/ui/Skeleton';
import SubmissionCard from '../../components/submissions/SubmissionCard';
import { submissionService } from '../../services/submissionService';

/**
 * Every paper this person has submitted, in full.
 *
 * This screen used to be the thin one — a grouped list of titles and status
 * pills, while the dashboard carried steppers, file sizes and review tallies.
 * That was backwards. The dashboard answers "does anything need me"; this
 * answers "what is going on with this paper", so the depth belongs here.
 *
 * `?focus=<id>` arrives from the dashboard. Rather than dropping someone into a
 * list and asking them to find their paper again, the focused card is pulled to
 * the top, outlined, and scrolled to.
 */
const MySubmissions = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const focusRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
      return;
    }
    loadSubmissions();
  }, [user]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      setError('');
      // The review-status variant, because this screen shows review progress
      // and camera-ready state. The plain call omits both.
      const data = await submissionService?.getAllUserSubmissionsWithReviewStatus(user?.id);
      setSubmissions(data || []);
    } catch (err) {
      console.error('Error loading submissions:', err);
      setError(err?.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  // Scroll the focused card into view once it has actually rendered.
  useEffect(() => {
    if (!focusId || loading) return;
    const el = document.getElementById(`submission-${focusId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusId, loading, submissions]);

  const counts = {
    all: submissions?.length || 0,
    action: submissions?.filter((s) => ['draft', 'revision_required'].includes(s?.status))?.length || 0,
    in_progress: submissions?.filter((s) => ['submitted', 'under_review'].includes(s?.status))?.length || 0,
    decided: submissions?.filter((s) => ['accepted', 'rejected'].includes(s?.status))?.length || 0,
  };

  const byFilter = (s) => {
    if (filter === 'action') return ['draft', 'revision_required'].includes(s?.status);
    if (filter === 'in_progress') return ['submitted', 'under_review'].includes(s?.status);
    if (filter === 'decided') return ['accepted', 'rejected'].includes(s?.status);
    return true;
  };

  const filtered = (submissions || []).filter(byFilter);

  // The focused paper first — it's why the person is here.
  const ordered = focusId
    ? [...filtered].sort((a, b) => (String(a?.id) === focusId ? -1 : String(b?.id) === focusId ? 1 : 0))
    : filtered;

  const clearFocus = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/user-dashboard')}
            iconName="ArrowLeft"
            iconPosition="left"
            iconSize={16}
            className="mb-4"
          >
            {t('mySubmissions.backToDashboard', 'Back to dashboard')}
          </Button>

          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-foreground">{t('mySubmissions.title')}</h1>
            {!loading && (
              <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 bg-muted text-xs font-semibold text-muted-foreground tabular-nums">
                {counts.all}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {t('mySubmissions.subtitle', { defaultValue: 'All your paper submissions across conferences' })}
          </p>
        </div>

        {focusId && !loading && (
          <div className="mb-4 flex items-center gap-2 text-sm">
            <Icon name="Crosshair" size={14} className="text-primary" />
            <span className="text-muted-foreground">
              {t('mySubmissions.focused', 'Showing one paper first.')}
            </span>
            <button onClick={clearFocus} className="text-primary hover:underline font-medium">
              {t('mySubmissions.showAllOrder', 'Show normal order')}
            </button>
          </div>
        )}

        {!loading && !error && submissions?.length > 0 && (
          <div className="mb-4 flex items-center gap-1 overflow-x-auto">
            {[
              { key: 'all', label: t('mySubmissions.filters.all') },
              { key: 'action', label: t('mySubmissions.filters.actionNeeded') },
              { key: 'in_progress', label: t('mySubmissions.filters.inProgress') },
              { key: 'decided', label: t('mySubmissions.filters.decided', 'Decided') },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                  filter === f.key
                    ? 'bg-card text-foreground shadow-sm border border-border'
                    : 'text-muted-foreground hover:text-foreground hover:bg-card/60'
                }`}
              >
                {f.label}
                {counts[f.key] > 0 && (
                  <span className={`ms-1.5 ${filter === f.key ? 'text-primary' : 'text-muted-foreground'}`}>
                    {counts[f.key]}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <ListSkeleton rows={3} />
          </div>
        )}

        {error && (
          <div className="bg-error-subtle border border-error-border rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-error" />
              <p className="text-error">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && submissions?.length === 0 && (
          <div className="bg-card rounded-lg border border-border">
            <EmptyState
              icon="FileText"
              title={t('mySubmissions.emptyState.message')}
              body={t('mySubmissions.emptyState.description', {
                defaultValue: 'Papers you submit to any conference show up here, with their review status.',
              })}
              action={{
                label: t('mySubmissions.emptyState.browseConferences'),
                icon: 'Search',
                onClick: () => navigate('/public-conference-board'),
              }}
            />
          </div>
        )}

        {!loading && !error && submissions?.length > 0 && ordered.length === 0 && (
          <div className="bg-card rounded-lg border border-border">
            <EmptyState filtered onClear={() => setFilter('all')} />
          </div>
        )}

        {!loading && !error && ordered.length > 0 && (
          <div className="space-y-4">
            {ordered.map((submission) => (
              <SubmissionCard
                key={submission?.id}
                submission={submission}
                onRefresh={loadSubmissions}
                focused={String(submission?.id) === focusId}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MySubmissions;
